import React from "react";
import ReactDOM from "react-dom/client";

/** @typedef {import('../../models/CfcCategoryModel.js').CfcCategoryItem } CfcCategoryItem */
/** @typedef {import('../../models/CfcScoreModel.js').CfcScoreItem } CfcScoreItem */
/** @typedef {import('../../models/CfcWodModel.js').CfcWodItem } CfcWodItem */
/** @typedef {import('../../services/CfcScoreService.js').CfcScoreObjForView } CfcScoreObjForView */

/**
 * @typedef {object} CfcScoresProps
 * @property {?string} id - ID du tournoi pour lequel on veut récupérer les inscriptions
 * @property {Array<CfcScoreObjForView>} cfcScores - Liste des scores
 * @property {Array<CfcCategoryItem>} cfcCategories - Liste des catégories
 * @property {Array<CfcWodItem>} cfcWods - Liste des wods
 * @property {{ cfcCategoryId: ?number, cfcWodId: ?number, searchValue: ?string }} search - Objet contenant les valeurs de recherche
 */

class CfcScores extends React.Component {
  /**
   * @param {CfcScoresProps} props
   */
  constructor(props) {
    super(props);
    this.state = {
      id: props.id || null,
      cfcScores: /** @type {Array<CfcScoreObjForView>} */ ([]),
      filteredCfcScores: /** @type {Array<CfcScoreObjForView>} */ ([]),
      cfcCategories: /** @type {Array<CfcCategoryItem>} */ ([]),
      cfcWods: /** @type {Array<CfcWodItem>} */ ([]),
      search: {
        cfcCategoryId: null,
        cfcWodId: null,
        searchValue: null
      }
    };
  }

  async componentDidMount() {
    const cfcScores = await this.fetchCfcScores();
    const cfcCategories = await this.fetchCfcCategories();
    const cfcWods = await this.fetchCfcWods();

    this.setState({ cfcScores, cfcCategories, cfcWods, filteredCfcScores: cfcScores });
  }

  /**
   * Permet de récupérer la liste des scores d'une compétition
   * @returns {Promise<Array<CfcScoreItem>>} - Liste des scores
   */
  async fetchCfcScores() {
    var response = await fetch(`/api/cfc-score/_search?cfcTournamentId=${this.state.id}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des catégories d'une compétition
   * @returns {Promise<Array<CfcCategoryItem>>} - Liste des catégories
   */
  async fetchCfcCategories() {
    var response = await fetch(`/api/cfc-category/search?cfcTournamentId=${this.state.id}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des wods d'une compétition
   * @returns {Promise<Array<CfcWodItem>>} - Liste des wods
   */
  async fetchCfcWods() {
    var response = await fetch(`/api/cfc-wod/search?cfcTournamentId=${this.state.id}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Lorsqu'on change la valeur du select des wods, on filtre les scores en fonction du WOD et de sa catégorie
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le select
   */
  onChangeOnSelect = event => {
    const cfcWodId = event.currentTarget.value ? parseInt(event.currentTarget.value, 10) : null;
    const cfcWod = this.state.cfcWods.find(wod => wod.id === cfcWodId);

    this.setState(
      {
        search: {
          ...this.state.search,
          cfcWodId,
          cfcCategoryId: cfcWod ? cfcWod.cfcCategoryId : null
        }
      },
      () => this.refreshList()
    );
  };

  /**
   * Lorsqu'on saisit une valeur dans le champ de recherche, on filtre les inscriptions en fonction de cette valeur
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le champ de recherche
   */
  onChangeOnInput = event => {
    const searchValue = event.currentTarget.value.toLowerCase();

    this.setState(
      {
        search: {
          ...this.state.search,
          searchValue
        }
      },
      () => this.refreshList()
    );
  };

  /**
   * Permet de filtrer la liste des scores en fonction de la catégorie et du wod sélectionnée
   */
  refreshList() {
    const { cfcCategoryId, cfcWodId, searchValue } = this.state.search;
    const filteredCfcScores = this.state.cfcScores.filter(cfcScore => {
      const matchesCategory = cfcCategoryId ? cfcScore.cfc_registration.cfcCategoryId === cfcCategoryId : true;
      const matchesWod = cfcWodId ? cfcScore.cfcWodId === cfcWodId : true;
      const matchesSearch = searchValue ? cfcScore.cfc_registration.nameStr.toLowerCase().includes(searchValue) : true;

      return matchesCategory && matchesWod && matchesSearch;
    });

    this.setState({ filteredCfcScores });
  }

  /**
   * Permet de changer l'état du score lorsqu'on clique sur le badge
   * @param {CfcScoreObjForView} cfcScore - Objet du score
   */
  onClickOnStateBadge(cfcScore) {
    const apiUrl = `/api/_changeboolean/cfc-score/${cfcScore.id}/state/${cfcScore.state ? 0 : 1}`;

    fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "gestiaweb"
      }
    })
      .then(response => response.json())
      .then(async data => {
        const cfcScores = await this.fetchCfcScores();

        this.setState({ cfcScores, filteredCfcScores: cfcScores });
      });
  }

  /**
   * Permet de rendre l'état du score sous forme de badge
   * @param {CfcScoreObjForView} cfcScore - Objet du score
   * @returns {React.ReactElement} - Badge représentant l'état du score
   */
  renderState(cfcScore) {
    return cfcScore.state ? (
      <div className="badge badge--success badge--clickable" onClick={this.onClickOnStateBadge.bind(this, cfcScore)}>
        Validé
      </div>
    ) : (
      <div className="badge badge--danger badge--clickable" onClick={this.onClickOnStateBadge.bind(this, cfcScore)}>
        Non validé
      </div>
    );
  }

  renderOptions() {
    return this.state.cfcCategories.map(cfcCategory => (
      <optgroup key={cfcCategory.id} label={cfcCategory.name}>
        {this.state.cfcWods
          .filter(cfcWod => cfcWod.cfcCategoryId === cfcCategory.id)
          .map(cfcWod => (
            <option key={cfcWod.id} value={cfcWod.id}>
              {cfcWod.name}
            </option>
          ))}
      </optgroup>
    ));
  }

  render() {
    return (
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gridGap: "16px", height: "100%" }}>
        <div className="flexline flexline--margin flexline--no-mobile">
          <select className="input-group__input input-group__input--with-small" onChange={this.onChangeOnSelect.bind(this)}>
            <option value="" key="all">
              Toutes
            </option>
            {this.renderOptions()}
          </select>
          <input type="text" className="input-group__input input-group__input--with-small" aria-label="Recherche" placeholder="Rechercher..." onChange={this.onChangeOnInput.bind(this)} />
        </div>
        <div className="table table--responsive" style={{ height: "100%", overflowY: "auto" }}>
          <table className="table__table table__table--td-bordered">
            <thead>
              <tr className="table__tr">
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">Équipe / Athlète</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">Categorie</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">Wod</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">Score</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">État</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">Type</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">Commentaires</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main">Liens vidéos</th>
                <th className="table__th table__th table__th-sticky table__th--main">Actions</th>
              </tr>
            </thead>
            <tbody>
              {this.state.filteredCfcScores.map((cfcScore, index) => (
                <tr key={index}>
                  <td className="table__td table__td--align-left" dangerouslySetInnerHTML={{ __html: cfcScore.cfc_registration.nameHtmlStr }}></td>
                  <td className="table__td table__td--align-left">{cfcScore.cfc_registration.cfc_category.name}</td>
                  <td className="table__td table__td--align-left">{cfcScore.cfc_wod.name}</td>
                  <td className="table__td table__td--align-left">{cfcScore.tiebreak ? `${cfcScore.score} (${cfcScore.tiebreak})` : cfcScore.score}</td>
                  <td className="table__td table__td--align-left">{this.renderState(cfcScore)}</td>
                  <td className="table__td table__td--align-left">{cfcScore.typeStr}</td>
                  <td className="table__td table__td--align-left">
                    {cfcScore.comment || cfcScore.private_comment ? (
                      <>
                        <span>{cfcScore.comment}</span>
                        <br />
                        <span>
                          <b>Commentaire privé :</b> {cfcScore.private_comment}
                        </span>
                      </>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td className="table__td table__td--align-left">
                    {cfcScore.videosLinks && cfcScore.videosLinks.length > 0 ? (
                      <ul>
                        {cfcScore.videosLinks.map((link, index) => (
                          <li key={index}>
                            <a href={link} target="_blank" rel="noopener noreferrer">
                              {link}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td className="table__td table__td--align-left">
                    <ul className="actionlist donthideactions">
                      <li>
                        <a className="btn btn--no-label btn--ultra-small btn--danger" data-dialog="true" data-dialog-danger="true" data-dialog-action-title="Supprimer" href={`/__delete/cfc-score/${cfcScore.id}?forcereturnurl=/cfc-score/__index/${this.state.id}`}>
                          <span className="material-icons">delete</span>
                        </a>
                      </li>
                      <li>
                        <a className="btn btn--no-label btn--ultra-small" href={`/cfc-score/___edit/${cfcScore.id}`}>
                          <span className="material-icons">edit</span>
                        </a>
                      </li>
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

var cfcScoresNode = document.getElementById("cfc-scores");
if (cfcScoresNode) {
  var root = ReactDOM.createRoot(cfcScoresNode);
  root.render(<CfcScores id={cfcScoresNode.getAttribute("data-id")} />);
}
